import { useState, useEffect } from 'react';
import { Calculator, Home, Bath, Bed, Sofa, UtensilsCrossed, Sparkles, Calendar, Save, FileText, User, Plus, X } from 'lucide-react';
import { IconTile, TINT } from '../components/ui/IconTile';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const CleaningCalculator = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    squareFeet: 1200,
    bathrooms: 2,
    bedrooms: 4,
    livingRooms: 1,
    kitchenSize: 'medium',
    masterBathFeatures: 'shower_tub',
    hasPets: false,
    cleaningSupplies: 'green',
    frequency: 'one_time',
    additionalRooms: [],
    cleanFridge: false
  });

  const [estimatedCost, setEstimatedCost] = useState(0);
  const [priceBreakdown, setPriceBreakdown] = useState([]);
  
  // Customer management state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  // Load customers on mount
  useEffect(() => {
    loadCustomers();
  }, []);

  const additionalRoomOptions = [
    { id: 'dining', label: 'Dining Room', price: 25 },
    { id: 'playroom', label: 'Play Room', price: 30 },
    { id: 'laundry', label: 'Laundry Room', price: 20 },
    { id: 'gym', label: 'Home Gym', price: 35 },
    { id: 'garage', label: 'Garage', price: 40 },
    { id: 'basement', label: 'Basement', price: 50 },
    { id: 'office', label: 'Home Office', price: 25 }
  ];

  const frequencyDiscounts = {
    weekly: { label: 'Weekly Service', discount: 0.15 },
    biweekly: { label: 'Bi-Weekly Service', discount: 0.10 },
    triweekly: { label: 'Tri-Weekly Service', discount: 0.08 },
    monthly: { label: 'Monthly Service', discount: 0.05 },
    one_time: { label: 'One Time Service', discount: 0 }
  };

  useEffect(() => {
    calculatePrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const loadCustomers = () => {
    const data = JSON.parse(localStorage.getItem('maidly_customers') || '[]');
    setCustomers(data);
  };

  const handleAddCustomer = (e) => {
    e.preventDefault();
    
    if (!newCustomerForm.name || !newCustomerForm.email || !newCustomerForm.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    const newCustomer = {
      ...newCustomerForm,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };

    const updatedCustomers = [...customers, newCustomer];
    setCustomers(updatedCustomers);
    localStorage.setItem('maidly_customers', JSON.stringify(updatedCustomers));
    
    setSelectedCustomer(newCustomer);
    setShowAddCustomerModal(false);
    setNewCustomerForm({ name: '', email: '', phone: '', address: '', notes: '' });
    
    toast.success('Customer added successfully');
  };

  const calculatePrice = () => {
    let basePrice = 0;
    const breakdown = [];

    // Square footage base rate ($0.15 per sq ft)
    const sqFtPrice = formData.squareFeet * 0.15;
    basePrice += sqFtPrice;
    breakdown.push({ item: `${formData.squareFeet} sq ft`, price: sqFtPrice });

    // Bathrooms ($30 per bathroom)
    const bathroomPrice = formData.bathrooms * 30;
    basePrice += bathroomPrice;
    breakdown.push({ item: `${formData.bathrooms} Bathroom(s)`, price: bathroomPrice });

    // Bedrooms ($25 per bedroom)
    const bedroomPrice = formData.bedrooms * 25;
    basePrice += bedroomPrice;
    breakdown.push({ item: `${formData.bedrooms} Bedroom(s)`, price: bedroomPrice });

    // Living rooms ($35 per living room)
    const livingPrice = formData.livingRooms * 35;
    basePrice += livingPrice;
    breakdown.push({ item: `${formData.livingRooms} Living Room(s)`, price: livingPrice });

    // Kitchen size
    const kitchenPrices = { small: 30, medium: 50, large: 70 };
    const kitchenPrice = kitchenPrices[formData.kitchenSize] || 50;
    basePrice += kitchenPrice;
    breakdown.push({ item: `${formData.kitchenSize.charAt(0).toUpperCase() + formData.kitchenSize.slice(1)} Kitchen`, price: kitchenPrice });

    // Master bathroom features
    const masterBathPrices = {
      shower_only: 10,
      tub_only: 10,
      shower_tub: 20,
      no_appliances: 0
    };
    const masterBathPrice = masterBathPrices[formData.masterBathFeatures] || 0;
    if (masterBathPrice > 0) {
      basePrice += masterBathPrice;
      breakdown.push({ item: 'Master Bath Features', price: masterBathPrice });
    }

    // Pets ($15 extra)
    if (formData.hasPets) {
      basePrice += 15;
      breakdown.push({ item: 'Pet Hair Cleanup', price: 15 });
    }

    // Cleaning supplies
    const supplyPrices = { green: 20, company: 10, client: 0 };
    const supplyPrice = supplyPrices[formData.cleaningSupplies] || 0;
    if (supplyPrice > 0) {
      basePrice += supplyPrice;
      breakdown.push({ item: 'Cleaning Supplies', price: supplyPrice });
    }

    // Additional rooms
    formData.additionalRooms.forEach(roomId => {
      const room = additionalRoomOptions.find(r => r.id === roomId);
      if (room) {
        basePrice += room.price;
        breakdown.push({ item: room.label, price: room.price });
      }
    });

    // Clean fridge
    if (formData.cleanFridge) {
      basePrice += 25;
      breakdown.push({ item: 'Clean Inside Refrigerator', price: 25 });
    }

    // Apply frequency discount
    const discountRate = frequencyDiscounts[formData.frequency]?.discount || 0;
    let finalPrice = basePrice;
    
    if (discountRate > 0) {
      const discount = basePrice * discountRate;
      breakdown.push({ item: `${frequencyDiscounts[formData.frequency].label} (${(discountRate * 100).toFixed(0)}% off)`, price: -discount });
      finalPrice = basePrice - discount;
    }

    setPriceBreakdown(breakdown);
    setEstimatedCost(finalPrice);
  };

  const toggleAdditionalRoom = (roomId) => {
    if (formData.additionalRooms.includes(roomId)) {
      setFormData({
        ...formData,
        additionalRooms: formData.additionalRooms.filter(id => id !== roomId)
      });
    } else {
      setFormData({
        ...formData,
        additionalRooms: [...formData.additionalRooms, roomId]
      });
    }
  };

  const saveAsQuote = () => {
    // Check if customer is selected
    if (!selectedCustomer) {
      toast.error('Please select a customer first');
      return;
    }

    // Create line items from breakdown
    const items = priceBreakdown.filter(item => item.price > 0).map(item => ({
      id: crypto.randomUUID(),
      description: item.item,
      quantity: 1,
      rate: item.price,
      amount: item.price
    }));

    const settings = JSON.parse(localStorage.getItem('maidly_settings') || '{}');
    const taxRate = Number(settings.taxRate ?? settings.salesTaxRate ?? 8);
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const tax = (subtotal * taxRate) / 100;

    const estimates = JSON.parse(localStorage.getItem('maidly_estimates') || '[]');
    const numericPart = String(estimates.length + 1).padStart(4, '0');

    const estimate = {
      id: crypto.randomUUID(),
      estimateNumber: `EST-${numericPart}`,
      customerId: selectedCustomer.id,
      customerName: selectedCustomer.name,
      customerEmail: selectedCustomer.email,
      customerPhone: selectedCustomer.phone,
      customerAddress: selectedCustomer.address,
      items,
      subtotal,
      taxRate,
      tax,
      discount: 0,
      depositAmount: 0,
      total: subtotal + tax,
      balanceDue: subtotal + tax,
      status: 'draft',
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      createdAt: new Date().toISOString(),
      notes: `Cleaning service estimate · ${formData.squareFeet} sq ft, ${formData.bedrooms} bed, ${formData.bathrooms} bath`,
      terms: 'This estimate is valid for 30 days.',
      fromCalculator: true,
      recurring: false,
      recurringFrequency: 'monthly',
    };

    localStorage.setItem('maidly_estimates', JSON.stringify([estimate, ...estimates]));
    // Wake any open Estimates list so it picks up the new row instantly.
    window.dispatchEvent(new CustomEvent('maidly:estimates-updated', { detail: { source: 'calculator', estimateId: estimate.id } }));

    toast.success(`Estimate ${estimate.estimateNumber} created for ${selectedCustomer.name}`);
    setTimeout(() => navigate('/invoices?tab=estimates'), 500);
  };

  const createInvoice = () => {
    // Check if customer is selected
    if (!selectedCustomer) {
      toast.error('Please select a customer first');
      return;
    }

    // Create line items from breakdown
    const items = priceBreakdown.filter(item => item.price > 0).map(item => ({
      description: item.item,
      quantity: 1,
      rate: item.price,
      amount: item.price
    }));

    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const tax = subtotal * 0.08;

    const invoices = JSON.parse(localStorage.getItem('maidly_invoices') || '[]');
    
    const invoice = {
      id: Date.now().toString(),
      invoiceNumber: `INV-${new Date().getFullYear()}-${String(invoices.length + 1).padStart(3, '0')}`,
      customerId: selectedCustomer.id,
      customerName: selectedCustomer.name,
      customerEmail: selectedCustomer.email,
      customerPhone: selectedCustomer.phone,
      customerAddress: selectedCustomer.address,
      items: items,
      subtotal: subtotal,
      taxRate: 8,
      tax: tax,
      discount: 0,
      total: subtotal + tax,
      status: 'draft',
      dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date().toISOString(),
      notes: `Cleaning service - ${formData.squareFeet} sq ft, ${formData.bedrooms} bed, ${formData.bathrooms} bath, ${frequencyDiscounts[formData.frequency]?.label}`,
      payments: [],
      terms: 'Payment due within 15 days',
      depositAmount: 0,
      recurring: false
    };

    // Save to invoices
    const updated = [...invoices, invoice];
    localStorage.setItem('maidly_invoices', JSON.stringify(updated));

    toast.success(`Invoice created for ${selectedCustomer.name}!`);
    setTimeout(() => navigate('/invoices'), 500);
  };

  return (
    <div data-testid="cleaning-calculator-page" style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <IconTile icon={Calculator} tint={TINT.calculator} size={44} />
          House Cleaning Cost Calculator
        </h1>
        <p style={{ color: '#6b7280' }}>Get an instant estimate for your cleaning service</p>
      </div>

      {/* Customer Selection Section */}
      <div
        className="card"
        style={{
          marginBottom: '2rem',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderLeft: `4px solid ${TINT.calculator}`,
          color: '#0f172a',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', margin: 0 }}>
            <IconTile icon={User} tint={TINT.customers} size={30} />
            Select Customer
          </h2>
          {selectedCustomer && (
            <button
              onClick={() => setSelectedCustomer(null)}
              style={{
                padding: '0.4rem 0.75rem',
                background: '#f1f5f9',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                color: '#475569',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>

        {selectedCustomer ? (
          <div style={{ 
            padding: '1rem', 
            background: '#f8fafc', 
            borderRadius: '10px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, marginBottom: '0.5rem', color: '#0f172a' }}>
              {selectedCustomer.name}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#475569' }}>
              <div>📧 {selectedCustomer.email}</div>
              <div>📱 {selectedCustomer.phone}</div>
              {selectedCustomer.address && <div>📍 {selectedCustomer.address}</div>}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <select
              onChange={(e) => {
                const customer = customers.find(c => c.id === e.target.value);
                setSelectedCustomer(customer);
              }}
              style={{
                flex: 1, minWidth: 220,
                padding: '0.7rem 0.85rem',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                fontSize: '0.95rem',
                fontWeight: 500,
                color: '#0f172a',
                background: '#fff',
                cursor: 'pointer',
              }}
              value=""
            >
              <option value="">Choose existing customer...</option>
              {customers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} - {customer.email}
                </option>
              ))}
            </select>
            
            <button
              onClick={() => setShowAddCustomerModal(true)}
              data-testid="calc-add-customer-button"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.55rem 1rem 0.55rem 0.55rem',
                background: TINT.customers, color: '#fff',
                border: 'none', borderRadius: 12,
                fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
                whiteSpace: 'nowrap', letterSpacing: 0.1,
                boxShadow: `0 6px 16px ${TINT.customers}55`,
                transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#db2777';
                e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.customers}88`;
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = TINT.customers;
                e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.customers}55`;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <IconTile icon={Plus} tint="rgba(255,255,255,0.22)" size={30} />
              Add New
            </button>
          </div>
        )}
      </div>

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ width: '500px', maxWidth: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 600 }}>Add New Customer</h2>
              <button
                onClick={() => setShowAddCustomerModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleAddCustomer}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={newCustomerForm.name}
                  onChange={(e) => setNewCustomerForm({ ...newCustomerForm, name: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Email *
                </label>
                <input
                  type="email"
                  value={newCustomerForm.email}
                  onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Phone *
                </label>
                <input
                  type="tel"
                  value={newCustomerForm.phone}
                  onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })}
                  className="input"
                  required
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Address
                </label>
                <input
                  type="text"
                  value={newCustomerForm.address}
                  onChange={(e) => setNewCustomerForm({ ...newCustomerForm, address: e.target.value })}
                  className="input"
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Notes
                </label>
                <textarea
                  value={newCustomerForm.notes}
                  onChange={(e) => setNewCustomerForm({ ...newCustomerForm, notes: e.target.value })}
                  className="input"
                  rows="3"
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  type="button"
                  onClick={() => setShowAddCustomerModal(false)}
                  className="btn btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  Add Customer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '2rem' }}>
        {/* Left Column - Form */}
        <div>
          {/* About Your Home */}
          <div className="card" style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <IconTile icon={Home} tint="#0073ea" size={30} iconSize={16} />
              Tell us about your home
            </h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Total Area (sq ft)
                </label>
                <input
                  type="number"
                  value={formData.squareFeet}
                  onChange={(e) => setFormData({ ...formData, squareFeet: parseInt(e.target.value) || 0 })}
                  className="input"
                  min="0"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Bath size={16} />
                  Number of Bathrooms
                </label>
                <input
                  type="number"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: parseInt(e.target.value) || 0 })}
                  className="input"
                  min="0"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Bed size={16} />
                  Number of Bedrooms
                </label>
                <input
                  type="number"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: parseInt(e.target.value) || 0 })}
                  className="input"
                  min="0"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Sofa size={16} />
                  Number of Living Rooms
                </label>
                <input
                  type="number"
                  value={formData.livingRooms}
                  onChange={(e) => setFormData({ ...formData, livingRooms: parseInt(e.target.value) || 0 })}
                  className="input"
                  min="0"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <UtensilsCrossed size={16} />
                  Kitchen Size
                </label>
                <select
                  value={formData.kitchenSize}
                  onChange={(e) => setFormData({ ...formData, kitchenSize: e.target.value })}
                  className="input"
                >
                  <option value="small">Small (0 - 150 sq ft)</option>
                  <option value="medium">Medium (151 - 250 sq ft)</option>
                  <option value="large">{'Large (>250 sq ft)'}</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Master Bathroom Features
                </label>
                <select
                  value={formData.masterBathFeatures}
                  onChange={(e) => setFormData({ ...formData, masterBathFeatures: e.target.value })}
                  className="input"
                >
                  <option value="shower_only">Shower Only</option>
                  <option value="tub_only">Tub Only</option>
                  <option value="shower_tub">Separate Shower and Tub</option>
                  <option value="no_appliances">No Appliances</option>
                </select>
              </div>
            </div>

            <div style={{ marginTop: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.hasPets}
                  onChange={(e) => setFormData({ ...formData, hasPets: e.target.checked })}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Do you have pets?</span>
              </label>
            </div>
          </div>

          {/* Service & Extras */}
          <div className="card" style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <IconTile icon={Sparkles} tint={TINT.calculator} size={30} iconSize={16} />
              Select your service &amp; extras
            </h2>

            <div style={{ display: 'grid', gap: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  Cleaning Supplies
                </label>
                <select
                  value={formData.cleaningSupplies}
                  onChange={(e) => setFormData({ ...formData, cleaningSupplies: e.target.value })}
                  className="input"
                >
                  <option value="green">Green Cleaning (+$20)</option>
                  <option value="company">Company's Supplies (+$10)</option>
                  <option value="client">Client's Supplies (No charge)</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Calendar size={16} />
                  Cleaning Frequency
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="input"
                >
                  <option value="weekly">Weekly Service (15% off)</option>
                  <option value="biweekly">Bi-Weekly Service (10% off)</option>
                  <option value="triweekly">Tri-Weekly Service (8% off)</option>
                  <option value="monthly">Monthly Service (5% off)</option>
                  <option value="one_time">One Time Service</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.75rem' }}>
                  Additional Rooms
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  {additionalRoomOptions.map(room => (
                    <label key={room.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.additionalRooms.includes(room.id)}
                        onChange={() => toggleAdditionalRoom(room.id)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <span style={{ fontSize: '0.875rem' }}>{room.label} (+${room.price})</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.cleanFridge}
                    onChange={(e) => setFormData({ ...formData, cleanFridge: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Clean inside the refrigerator? (+$25)</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Price Breakdown */}
        <div>
          <div
            className="card"
            style={{
              position: 'sticky', top: '2rem',
              background: 'linear-gradient(135deg, #0073ea 0%, #0059b8 100%)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.18)',
              boxShadow: '0 12px 32px rgba(0,115,234,0.35), 0 0 0 1px rgba(255,255,255,0.15) inset',
            }}
          >
            <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '1rem', color: '#fff' }}>Estimated Cost</h2>
            
            <div style={{ fontSize: '3rem', fontWeight: 700, marginBottom: '1.5rem', color: '#fff' }}>
              ${estimatedCost.toFixed(2)}
            </div>

            {priceBreakdown.length > 0 && (
              <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.75rem', color: '#fff' }}>Price Breakdown:</div>
                {priceBreakdown.map((item, index) => (
                  <div key={`${item.item}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', marginBottom: '0.5rem', color: '#fff', opacity: 0.9 }}>
                    <span>{item.item}</span>
                    <span style={{ fontWeight: 600 }}>${item.price.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                onClick={createInvoice}
                data-testid="calc-create-invoice-button"
                style={{
                  width: '100%',
                  padding: '0.85rem 1rem',
                  background: TINT.calculator, color: '#0f172a',
                  border: 'none', borderRadius: 12,
                  fontSize: '0.95rem', fontWeight: 700,
                  cursor: 'pointer', letterSpacing: 0.1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: `0 6px 16px ${TINT.calculator}55`,
                  transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fbbf24';
                  e.currentTarget.style.boxShadow = `0 10px 22px ${TINT.calculator}88`;
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = TINT.calculator;
                  e.currentTarget.style.boxShadow = `0 6px 16px ${TINT.calculator}55`;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconTile icon={FileText} tint="rgba(15,23,42,0.15)" size={30} style={{ color: '#0f172a' }} />
                Create Invoice
              </button>

              <button
                onClick={saveAsQuote}
                data-testid="calc-save-estimate-button"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255, 255, 255, 0.18)',
                  color: '#fff',
                  border: '1px solid rgba(255, 255, 255, 0.35)',
                  borderRadius: 12,
                  fontSize: '0.9rem', fontWeight: 700, letterSpacing: 0.1,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  transition: 'background 0.2s ease, transform 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.18)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <IconTile icon={Save} tint="rgba(255,255,255,0.22)" size={30} />
                Save as Estimate
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CleaningCalculator;
